import { Grid } from "@radix-ui/themes";
import cx from "classnames";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Expandable } from "./Expandable";
import { Markdown } from "./Markdown";
import { SpreadsheetView } from "./SpreadsheetView";

import { flagVariant } from "../config/featureFlags";
import {
  isKnowledgeDataRecordPropertyEmpty,
  KnowledgeDataRecordProperty,
} from "../utils/parsers/knowledge-db";
import { isValidUrl } from "../utils/validation";

export type PreviewProps = {
  url?: string;
  pageNumber?: number;
  text?: string;
  textPrefix?: string;
  knowledgeDataRecord?: KnowledgeDataRecordProperty[];
  spreadsheetComments?: {
    _id?: string;
    description?: string;
    cellAnchor?: {
      sheetIndex: number;
      sheetName?: string;
      range: string;
    };
    disposition?: string;
  }[];
  onSpreadsheetCommentClick?: (commentId: string) => void;
};

export function urlToPreviewType(
  url?: string,
  text?: string,
  knowledgeDataRecord?: KnowledgeDataRecordProperty[],
) {
  if (!url) {
    if (text) return "text";
    if (knowledgeDataRecord) return "knowledge";
    return "unknown";
  }
  if (/\?|#/.test(url)) return "iframe";
  if (url.endsWith(".pdf")) return "application/pdf";
  if (url.endsWith(".md")) return "text/markdown";
  if (url.endsWith(".txt")) return "text/plain";
  if (/\.(?:jpe?g|png|gif|bmp|tiff|webp)$/.test(url))
    return "image/" + url.split(".").pop();
  if (/\.(?:mp4|webm|ogv)$/.test(url)) return "video/" + url.split(".").pop();
  // Spreadsheet extensions render in the SpreadsheetView when the
  // spreadsheet-extraction flag is on; otherwise they fall through to
  // "download" (preserving the legacy behavior under shadow mode).
  if (/(?<!\?|#)\.(?:xlsx?|xlsm|xlsb|ods|csv|tsv)$/.test(url)) {
    return flagVariant("spreadsheet-extraction") === "full"
      ? "spreadsheet"
      : "download";
  }
  if (/(?<!\?|#)\.(?:docx?|pptx?|odt|odp)$/.test(url)) return "download";
  return "iframe";
}

export function Preview(props: PreviewProps) {
  const { t } = useTranslation("components");
  const {
    url,
    pageNumber,
    text,
    textPrefix,
    knowledgeDataRecord,
    spreadsheetComments,
    onSpreadsheetCommentClick,
  } = props;
  const previewType = urlToPreviewType(url, text, knowledgeDataRecord);

  const [mdText, setMdText] = useState("Loading...");
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string>();
  // why: the effect below only re-runs on [url, previewType], so a closure over
  // the pdfBlobUrl STATE is always the value from the render that started the
  // fetch — i.e. undefined — and never revokes anything. The ref is written as
  // soon as the blob URL exists, so cleanup can release the real one.
  const pdfBlobUrlRef = useRef<string>();

  useEffect(() => {
    // why: a superseded response must not win. Without this flag a slow fetch
    // for the previous url resolves after the current one and paints the wrong
    // document's text under the new citation.
    let cancelled = false;

    // why: the pane renders whatever mdText still holds, so the PREVIOUS
    // document's text stays on screen under the new citation for the whole
    // duration of the new fetch. The cancelled flag above only stops a
    // superseded response from winning; it does nothing about content that
    // already won and is still mounted. Dropping it up front puts the pane
    // back on its loading state for every url change, mirroring the PDF
    // effect below.
    if (previewType === "text/markdown" && url) {
      setMdText("Loading...");
    }

    const fetchMarkdown = async () => {
      if (previewType !== "text/markdown" || !url) return;
      try {
        const response = await fetch(
          url +
            "?response-content-disposition=inline&response-content-type=text%2Fmarkdown",
        );
        // why: S3 answers a rejected GET with an XML/HTML error body, so an
        // unchecked read renders "AccessDenied" as if it were the file.
        if (!response.ok) {
          throw new Error(`status ${response.status}`);
        }
        const body = await response.text();
        if (cancelled) return;
        setMdText(body);
      } catch (error) {
        if (cancelled) return;
        setMdText(`Error loading file: ${error}`);
      }
    };
    fetchMarkdown();

    return () => {
      cancelled = true;
    };
  }, [url, previewType]);

  useEffect(() => {
    let cancelled = false;

    const releaseBlobUrl = () => {
      if (pdfBlobUrlRef.current) {
        URL.revokeObjectURL(pdfBlobUrlRef.current);
        pdfBlobUrlRef.current = undefined;
      }
    };

    // why: the pane renders whatever pdfBlobUrl still holds, so the PREVIOUS
    // document stays mounted under the new citation while the new one loads —
    // and stays there for good if the new fetch is rejected, since the catch
    // below only logs. Dropping it up front puts the pane back on its loading
    // state for every url change.
    releaseBlobUrl();
    setPdfBlobUrl(undefined);

    const fetchPdf = async () => {
      if (previewType !== "application/pdf" || !url) return;

      try {
        const response = await fetch(
          url +
            "?response-content-disposition=inline&response-content-type=application%2Fpdf",
        );
        // why: without this a 403/404 error body is turned into a Blob and
        // mounted as the PDF object URL. Throwing keeps that body out of the
        // viewer: the catch below records the status and the pane stays on its
        // loading state instead of embedding the S3 error document.
        if (!response.ok) {
          throw new Error(`status ${response.status}`);
        }
        const blob = await response.blob();
        if (cancelled) return;
        const blobUrl = URL.createObjectURL(blob);
        pdfBlobUrlRef.current = blobUrl;
        setPdfBlobUrl(blobUrl);
      } catch (e) {
        console.error("Error loading PDF:", e);
      }
    };
    fetchPdf();

    return () => {
      cancelled = true;
      releaseBlobUrl();
    };
  }, [url, previewType]);

  return (
    <Grid
      justify={url ? "center" : "start"}
      className={cx("w-full", {
        "h-[calc(100%_-_48px)]": previewType !== "knowledge",
      })}
    >
      {previewType === "application/pdf" ? (
        <object
          data={
            pdfBlobUrl ? `${pdfBlobUrl}#page=${pageNumber ?? 1}` : undefined
          }
          type="application/pdf"
          width="100%"
          height="100%"
          className="border-puntt-neutral-6 grow border"
        >
          {!pdfBlobUrl ? (
            t("preview.loadingPdf")
          ) : (
            <>
              Your browser does not support PDF embedding.{" "}
              <a
                data-testid="preview-pdf-download-link"
                href={url}
                target="_blank"
                rel="noreferrer"
                className="text-puntt-accent-12 hover:text-puntt-accent-11 active:text-puntt-accent-10"
              >
                {t("preview.downloadInstead")}
              </a>
            </>
          )}
        </object>
      ) : previewType === "knowledge" ? (
        <>
          {knowledgeDataRecord
            ?.filter((field) => !isKnowledgeDataRecordPropertyEmpty(field))
            .map((field, i) => (
              <div className="mb-2 gap-1" key={i} data-name={field.name}>
                <div className="font-bold">{field.name}:</div>
                <div>
                  {typeof field.value === "string" &&
                  isValidUrl(field.value) ? (
                    urlToPreviewType(field.value) !== "iframe" ? (
                      <Preview url={field.value} />
                    ) : (
                      <a
                        data-testid="preview-knowledge-record-link"
                        href={field.value}
                        target="_blank"
                        rel="noreferrer"
                        className="text-puntt-accent-12 hover:text-puntt-accent-11 active:text-puntt-accent-10"
                      >
                        {field.value}
                      </a>
                    )
                  ) : typeof field.value === "string" &&
                    !isNaN(new Date(field.value).getTime()) ? (
                    new Date(field.value).toLocaleString()
                  ) : (
                    <Expandable
                      onExpand={async () => {
                        if (typeof field.value !== "string") return;
                        try {
                          return JSON.stringify(
                            JSON.parse(field.value),
                            null,
                            2,
                          );
                        } catch {
                          // Don't change the text if it's not JSON
                        }
                      }}
                    >
                      {field.value?.toLocaleString()}
                    </Expandable>
                  )}
                </div>
              </div>
            ))}
        </>
      ) : previewType.startsWith("image/") ? (
        <img src={url} alt={t("preview.citationSource")} />
      ) : previewType.startsWith("video/") ? (
        // eslint-disable-next-line jsx-a11y/media-has-caption
        <video src={url} controls preload="metadata">
          Your browser does not support video embedding.{" "}
          <a
            data-testid="preview-video-download-link"
            href={url}
            target="_blank"
            rel="noreferrer"
            className="text-puntt-accent-12 hover:text-puntt-accent-11 active:text-puntt-accent-10"
          >
            {t("preview.downloadInstead")}
          </a>
        </video>
      ) : previewType === "spreadsheet" ? (
        url ? (
          <SpreadsheetView
            url={url}
            comments={spreadsheetComments}
            onCommentClick={onSpreadsheetCommentClick}
          />
        ) : (
          <p>{t("preview.noSpreadsheetUrlProvided")}</p>
        )
      ) : previewType === "download" ? (
        <a
          data-testid="preview-download-link"
          href={url}
          download
          className="text-puntt-accent-12 hover:text-puntt-accent-11 active:text-puntt-accent-10"
        >
          Download {url?.split("/").pop()}
        </a>
      ) : previewType === "text/plain" ? (
        <object
          data={url}
          type="text/plain"
          width="100%"
          height="100%"
          className="border-puntt-neutral-6 grow border"
        >
          Your browser does not support plain text embedding.{" "}
          <a
            data-testid="preview-text-download-link"
            href={url}
            target="_blank"
            rel="noreferrer"
            className="text-puntt-accent-12 hover:text-puntt-accent-11 active:text-puntt-accent-10"
          >
            {t("preview.downloadInstead")}
          </a>
        </object>
      ) : previewType === "text/markdown" ? (
        <Markdown>{mdText}</Markdown>
      ) : previewType === "iframe" ? (
        <iframe
          src={url}
          title={t("preview.citationSource")}
          width="100%"
          height="100%"
          className="border-puntt-neutral-6 grow border"
        />
      ) : previewType === "text" ? (
        <article>
          {textPrefix && (
            <p className="pb-2 text-puntt-neutral-gray-11">
              <em>{textPrefix}</em>
            </p>
          )}
          <Markdown>{text}</Markdown>
        </article>
      ) : (
        <p>{t("preview.sorryWeCanAposT")}</p>
      )}
    </Grid>
  );
}
